/**
 * Same-origin fetch client for the public, UNAUTHENTICATED `/api/signup/*`
 * endpoints (`functions/src/api/signup/index.ts`, Hosting rewrite in
 * `firebase.json`). No bearer token — this runs before any tenant/session
 * exists, same transport as `../lib/hub-client.ts` (the Access Hub's own
 * pre-auth client).
 *
 * Unlike `hub-client.ts` (which just throws a generic `Error` on a non-ok
 * response), the signup backend returns a discriminated `{ error: string }`
 * body on every failure (throttle, expired code, already-verified, ...) that
 * the UI needs to branch on to show the right message — so this client
 * parses that body into a typed `SignupApiError` the page can inspect via
 * `.code` (see `functions/src/api/signup/index.ts` for the exact codes each
 * endpoint can return).
 *
 * Every call carries the App Check token (C-16): without it, once the switch is
 * on, the backend answers 401 `app_check_failed`.
 */

import { getAppCheckToken } from "../lib/auth-actions";
import { appCheckHeader } from "../lib/app-check";

/** Every `error` string the three `/signup/*` endpoints can return. */
const SIGNUP_ERROR_CODES = [
  "invalid_body",
  "rate_limited",
  "not_found",
  "already_verified",
  "expired",
  "invalid_code",
  "not_verified",
  "provisioning_failed",
  "app_check_failed",
] as const;

export type SignupErrorCode = (typeof SIGNUP_ERROR_CODES)[number] | "unknown";

function isKnownSignupErrorCode(value: unknown): value is (typeof SIGNUP_ERROR_CODES)[number] {
  return typeof value === "string" && (SIGNUP_ERROR_CODES as readonly string[]).includes(value);
}

/** Typed error thrown on any non-2xx `/api/signup/*` response. */
export class SignupApiError extends Error {
  readonly status: number;
  readonly code: SignupErrorCode;

  constructor(status: number, code: SignupErrorCode) {
    super(`signup api error: ${status} ${code}`);
    this.name = "SignupApiError";
    this.status = status;
    this.code = code;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json", ...appCheckHeader(await getAppCheckToken()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const parsed = (await res.json().catch(() => ({}))) as { error?: unknown };
    const code: SignupErrorCode = isKnownSignupErrorCode(parsed.error) ? parsed.error : "unknown";
    throw new SignupApiError(res.status, code);
  }
  return (await res.json()) as T;
}

export function startSignup(input: {
  email: string;
  companyName: string;
  structureName: string;
  locale: "it" | "en";
  consent: { tos: true; privacy: true };
}): Promise<{ signupId: string }> {
  return postJson("/api/signup/start", input);
}

export function verifySignup(input: { signupId: string; code: string }): Promise<{ ok: true }> {
  return postJson("/api/signup/verify", input);
}

export function completeSignup(input: {
  signupId: string;
  ownerDisplayName: string;
  password: string;
}): Promise<{ tenantId: string; customToken: string }> {
  return postJson("/api/signup/complete", input);
}
